<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { isEmpty } from 'lodash';

import { YardTaskCheckLocationOutcome } from '@nova/core';
import YardAssetPresenterV2 from '@satellite/components/elements/presenters/YardAssetPresenterV2.vue';

import type { InventoryAssetRow } from '@/composables/useYardInventory';
import type { YardAssetDetails } from '@satellite/composables/useYardAssetPresenter';

const props = defineProps<{
  row: InventoryAssetRow;
}>();

defineEmits<{
  click: [row: InventoryAssetRow];
}>();

const { t } = useI18n();

const isConfirmed = computed(() => props.row.outcome === YardTaskCheckLocationOutcome.CONFIRMED);
const isMissing = computed(() => props.row.outcome === YardTaskCheckLocationOutcome.MISSING);

const statusIcon = computed(() => {
  if (isConfirmed.value) {
    return 'mdi-checkbox-marked';
  }
  if (isMissing.value) {
    return 'mdi-close-box';
  }
  return 'mdi-checkbox-blank-outline';
});

const statusColor = computed(() => {
  if (isConfirmed.value) {
    return 'success';
  }
  if (isMissing.value) {
    return 'error';
  }
  return 'neutral-80';
});

// Show the location whenever the row actually carries one, regardless of which bucket it's in
// (a row can have a known area/spot even outside an `area` bucket).
const hasAreaLocation = computed(
  () => !isEmpty(props.row.area?.name) && !isEmpty(props.row.spot?.code)
);

/**
 * The BFF row carries only the lean asset fields the chip needs (id/code/type). Seed it as the
 * presenter's authoritative `initial-asset-data` (with `staleTime: Infinity` the presenter won't
 * refetch), so this single boundary cast stands in for the full `YardAssetDetails` entity.
 */
const assetInitialData = computed(() => props.row.assetContainer as unknown as YardAssetDetails);
</script>

<template>
  <v-card
    v-ripple
    variant="flat"
    rounded
    border
    class="inventory-row d-flex align-center gap-3 px-4 py-4 w-100 text-start"
    :class="{
      'border-success': isConfirmed,
      'border-error': isMissing,
      'inventory-row--pending': !isConfirmed && !isMissing
    }"
    :data-testid="`yard-inventory-row-${row.assetContainer.id}`"
    @click="$emit('click', row)">
    <v-icon
      class="flex-shrink-0"
      size="24"
      :color="statusColor"
      :icon="statusIcon"
      :data-testid="`yard-inventory-row-${row.assetContainer.id}-status`" />

    <yard-asset-presenter-v2
      class="inventory-row__identity d-flex align-center gap-2 flex-shrink-0"
      :asset-id="row.assetContainer.id"
      :initial-asset-data="assetInitialData"
      :display-fields="['code', 'typeChip']"
      :asset-code-props="{ class: 'typography-body-md-bold text-no-wrap' }"
      :asset-type-chip-props="{ class: 'px-2 flex-shrink-0', density: 'compact', size: 'small' }" />

    <div class="d-flex align-center gap-1 flex-fill text-truncate text--color-text-secondary">
      <v-icon size="16" icon="mdi-map-marker-outline" class="flex-shrink-0" />
      <span class="typography-body-md text-truncate">
        <template v-if="hasAreaLocation">
          {{ row.area?.name }} –
          <strong>{{ row.spot?.code }}</strong>
        </template>
        <template v-else>{{ t('yardInventory.row.unassigned') }}</template>
      </span>
    </div>

    <v-icon class="flex-shrink-0 text--color-text-tertiary" size="20" icon="mdi-chevron-right" />
  </v-card>
</template>

<style lang="scss" scoped>
.inventory-row {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  --v-border-opacity: 1;
  transition: background-color 120ms ease;
}

.inventory-row:hover {
  background-color: rgb(var(--v-theme-neutral-10));
}

.inventory-row--pending {
  --v-border-color: var(--v-theme-neutral-40);
}
</style>
